/**
 * Field Report / job sheet (reference/Job_sheet.pdf), laid out like the paper form. Labor comes
 * from the time punches (one line each, D8); Work Performed from the notes. The office-use
 * billing boxes are set here by Office/Manager.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { dateTime } from '../../lib/format';
import type { FieldReportView } from '../../lib/form-types';
import { useSettings } from '../../lib/hooks';
import { Button, Card, ErrorText, Field, Input, Loading } from '../../components/ui';
import { SigImage } from './SignaturesTab';

export function FieldReportTab({ woId, me, readOnly, onChange }: { woId: number; me: Me; readOnly: boolean; onChange: () => void }) {
  const qc = useQueryClient();
  const settings = useSettings();
  const key = ['wo-field-report', woId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<FieldReportView>(`/api/work-orders/${woId}/field-report`) });
  const [error, setError] = useState<string | null>(null);
  if (q.isLoading || !q.data) return <Loading />;
  const r = q.data;
  const canBill = me.role !== 'MECHANIC' && !readOnly;

  const setBilling = async (code: string) => {
    try {
      await api.patch(`/api/work-orders/${woId}`, { billingType: code });
      await q.refetch();
      onChange();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <div className="space-y-4">
      <ErrorText error={error} />
      <Card title={<span>FIELD REPORT <span className="ml-3 text-slate-500">S/O {r.so}</span></span>}>
        <div className="grid gap-4 text-sm md:grid-cols-2">
          <section>
            <h3 className="mb-1 text-xs font-bold uppercase text-slate-500">Customer data</h3>
            <div>Name: <b>{r.customer.name}</b></div>
            <div>Phone: {r.customer.phone ?? '—'}</div>
          </section>
          <section>
            <h3 className="mb-1 text-xs font-bold uppercase text-slate-500">Equipment data</h3>
            <div>Make: {r.equipment.make} · M/N: {r.equipment.model}</div>
            <div>S/N: {r.equipment.serialNumber} · EQ.#: {r.equipment.equipmentNo ?? '—'}</div>
            <div>Hour meter: {r.equipment.hourMeter ?? '—'}</div>
          </section>
          <div>Customer P/O: {r.customerPo ?? '—'}</div>
          <div>Machine location: {r.machineLocation ?? '—'}</div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded border border-slate-300 p-2 text-sm">
          <span className="font-bold">OFFICE USE</span>
          {settings.data?.billingTypes.map((b) => (
            <label key={b.code} className="flex items-center gap-1" title={b.label}>
              <input type="radio" name="billing" checked={r.billingType === b.code} disabled={!canBill} onChange={() => setBilling(b.code)} />
              {b.code}
            </label>
          ))}
        </div>
        <h3 className="mt-4 text-xs font-bold uppercase text-slate-500">Details of problem</h3>
        <p className="whitespace-pre-wrap text-sm">{r.problemDescription}</p>
        <h3 className="mt-4 text-xs font-bold uppercase text-slate-500">Work performed</h3>
        {r.workPerformed.length === 0 && <p className="text-sm text-slate-500">No Work Performed notes yet.</p>}
        <ol className="space-y-2 text-sm">
          {r.workPerformed.map((n) => (
            <li key={n.id}>
              <span className="text-xs text-slate-500">{dateTime(n.at)} · {n.by}</span>
              <p className="whitespace-pre-wrap">{n.body}</p>
              {n.corrections.map((c) => (
                <p key={c.id} className="ml-4 whitespace-pre-wrap text-amber-900">Correction ({dateTime(c.at)}, {c.by}): {c.body}</p>
              ))}
            </li>
          ))}
        </ol>
      </Card>
      <Entries woId={woId} r={r} readOnly={readOnly} onSaved={(v) => { qc.setQueryData(key, v); onChange(); }} />
      <Card title={`Labor — ${r.labor.totalHours.toFixed(2)} h`}>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500"><tr><th>Mechanic</th><th>Start</th><th>Finish</th><th className="text-right">Hours</th></tr></thead>
          <tbody className="divide-y">
            {r.labor.lines.map((l) => (
              <tr key={l.id}><td className="py-1">{l.mechanic}</td><td>{dateTime(l.start)}</td><td>{l.finish ? dateTime(l.finish) : 'clocked in'}</td><td className="text-right">{l.hours?.toFixed(2) ?? '—'}</td></tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t font-semibold"><td colSpan={3} className="py-1 text-right">Total</td><td className="text-right">{r.labor.totalHours.toFixed(2)}</td></tr></tfoot>
        </table>
        <p className="mt-1 text-xs text-slate-500">From the time clock — not typed. Fix times on the Time tab (Manager, with a reason).</p>
      </Card>
      <Card title="Certification and signatures">
        <p className="text-xs leading-relaxed text-slate-700">{r.certification.text} <span className="text-slate-400">(v{r.certification.version})</span></p>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <SigImage label="Customer signature" sig={r.signatures.customer} />
          <SigImage label="Technician signature" sig={r.signatures.technician} />
        </div>
      </Card>
    </div>
  );
}

function Entries({ woId, r, readOnly, onSaved }: { woId: number; r: FieldReportView; readOnly: boolean; onSaved: (v: FieldReportView) => void }) {
  const [f, setF] = useState({
    lubeEng: r.lubricants.eng ?? '',
    lubeTrans: r.lubricants.trans ?? '',
    lubeHyd: r.lubricants.hyd ?? '',
    lubeCoolant: r.lubricants.coolant ?? '',
    lubeOther: r.lubricants.other ?? '',
    mileage: r.mileage ?? '',
    meals: r.meals ?? '',
    motel: r.motel ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => { setSaved(false); setF({ ...f, [k]: e.target.value }); };
  return (
    <Card title="Lubricants and expenses" actions={!readOnly && <Button variant="secondary" onClick={async () => {
      try { onSaved(await api.put(`/api/work-orders/${woId}/field-report`, f)); setSaved(true); } catch (e) { setError(errorText(e)); }
    }}>{saved ? 'Saved ✓' : 'Save'}</Button>}>
      <ErrorText error={error} />
      <div className="grid gap-3 md:grid-cols-5">
        <Field label="Eng."><Input value={f.lubeEng} onChange={set('lubeEng')} disabled={readOnly} /></Field>
        <Field label="Trans."><Input value={f.lubeTrans} onChange={set('lubeTrans')} disabled={readOnly} /></Field>
        <Field label="Hyd."><Input value={f.lubeHyd} onChange={set('lubeHyd')} disabled={readOnly} /></Field>
        <Field label="Coolant"><Input value={f.lubeCoolant} onChange={set('lubeCoolant')} disabled={readOnly} /></Field>
        <Field label="Other"><Input value={f.lubeOther} onChange={set('lubeOther')} disabled={readOnly} /></Field>
        <Field label="Mileage"><Input value={f.mileage} onChange={set('mileage')} inputMode="decimal" disabled={readOnly} /></Field>
        <Field label="Meals ($)"><Input value={f.meals} onChange={set('meals')} inputMode="decimal" disabled={readOnly} /></Field>
        <Field label="Motel ($)"><Input value={f.motel} onChange={set('motel')} inputMode="decimal" disabled={readOnly} /></Field>
      </div>
    </Card>
  );
}
